/**
 * FadeImageTabs — custom element for the "Fade Image Tabs" section.
 *
 * Responsibilities:
 *  1. Tab switching — clicking a tab swaps the visible panel with a cross-fade.
 *  2. Fade-in — IntersectionObserver toggles .is-visible on each .ftt__fade
 *     element. Repeats every time the element re-enters the viewport.
 */

if (!customElements.get('fade-image-tabs')) {
  class FadeImageTabs extends HTMLElement {
    connectedCallback() {
      requestAnimationFrame(() => {
        this._initTabs();
        this._initFade();
      });
    }

    // ── 1. Tab switching ─────────────────────────────────────────────────────

    _initTabs() {
      this._tabs   = [...this.querySelectorAll('.ftt__tab')];
      this._panels = [...this.querySelectorAll('.ftt__panel')];
      this._descs  = [...this.querySelectorAll('.ftt__desc')];
      if (!this._tabs.length) return;

      this._tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          if (tab.classList.contains('is-active')) return;

          const panelId = tab.dataset.panel;
          const descId  = tab.dataset.desc;

          // Update active tab
          this._tabs.forEach((t) => t.classList.remove('is-active'));
          tab.classList.add('is-active');

          // Cross-fade image panels
          this._panels.forEach((p) => {
            p.classList.toggle('ftt__panel--active', p.id === panelId);
          });

          // Cross-fade description panels
          this._descs.forEach((d) => {
            d.classList.toggle('ftt__desc--active', d.id === descId);
          });
        });
      });
    }

    // ── 2. Fade-in ──────────────────────────────────────────────────────────

    _initFade() {
      if (this.dataset.fade !== 'true') return;

      const els = [...this.querySelectorAll('.ftt__fade')];
      if (!els.length) return;

      // Stagger each element's entrance delay
      els.forEach((el, i) => {
        el.style.transitionDelay = (i * 120) + 'ms';
      });

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Toggle each time — resets on exit, re-animates on re-entry
            entry.target.classList.toggle('is-visible', entry.isIntersecting);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
      );

      els.forEach((el) => observer.observe(el));
    }
  }

  customElements.define('fade-image-tabs', FadeImageTabs);
}
